import { Download, ExternalLink } from 'lucide-react'
import * as React from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { fetchRequestPhoto, type RequestPhoto } from '@/hooks/useProductRequests'

/** image/jpeg → jpg, so a saved file opens in whatever the OS associates. */
function extFromMime(mime: string): string {
  const sub = mime.split(';')[0].split('/')[1]?.trim().toLowerCase()
  if (!sub) return 'jpg'
  if (sub === 'jpeg') return 'jpg'
  if (sub === 'svg+xml') return 'svg'
  return sub
}

/**
 * Full-size view of a request's reference photo. The endpoint is auth-gated, so
 * the image can only be reached through the blob fetch — which is also why the
 * download link points at the object URL rather than the API path.
 */
export function PhotoLightbox({
  requestId,
  open,
  onOpenChange,
}: {
  requestId: number
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [photo, setPhoto] = React.useState<RequestPhoto | null>(null)
  const [failed, setFailed] = React.useState(false)

  // Refetched per open: the photo can be replaced (or purged) between views,
  // and the object URL is revoked on close, so there's nothing to keep.
  React.useEffect(() => {
    if (!open) return
    let cancelled = false
    let created: string | null = null
    fetchRequestPhoto(requestId)
      .then((p) => {
        if (cancelled) {
          URL.revokeObjectURL(p.url)
          return
        }
        created = p.url
        setPhoto(p)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    // Closing revokes the object URL, so the state it belongs to is dropped
    // with it — the next open starts from the loading state again.
    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
      setPhoto(null)
      setFailed(false)
    }
  }, [open, requestId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] flex max-h-[90vh] w-[92vw] flex-col overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">Reference photo</DialogTitle>
          <DialogDescription className="text-xs text-[var(--mute)]">
            Attached to request #{requestId}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 overflow-y-auto">
          {!photo && !failed && <Skeleton className="h-64 w-full" />}

          {failed && <p className="text-xs text-[var(--mute)]">No photo</p>}

          {photo && (
            <>
              <img
                src={photo.url}
                alt={`Reference photo for request ${requestId}`}
                className="max-h-[70vh] max-w-full self-center object-contain"
              />
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => window.open(photo.url, '_blank')}
                >
                  <ExternalLink className="h-3 w-3" />
                  Open in new tab
                </Button>
                <Button asChild variant="outline" size="sm" className="h-7 text-xs">
                  <a
                    href={photo.url}
                    download={`request-${requestId}-photo.${extFromMime(photo.mime)}`}
                  >
                    <Download className="h-3 w-3" />
                    Download
                  </a>
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
